/**
 * ModulesTab — ERP Workspace "Module Manager" tab.
 * Read-only view of UAMS modules. No editing — modules are UAMS canonical source.
 * Changes to modules must be made via the UAMS Master Manager, not here.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Search, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';

export function ModulesTab() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/modules`);
      const d = await r.json();
      setModules(Array.isArray(d) ? d : []);
    } catch (e) { erpToast.error(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return modules.filter(m => !q
      || m.ModuleName?.toLowerCase().includes(q)
      || String(m.ModuleId).includes(q));
  }, [modules, search]);

  const th = {
    padding: '7px 10px', textAlign: 'left', fontWeight: 700,
    color: 'var(--text-secondary)', fontSize: '0.7rem',
    textTransform: 'uppercase', letterSpacing: '.04em',
    whiteSpace: 'nowrap', background: 'var(--surface-2,#f8fafc)',
  };

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Info banner */}
      <div style={{
        background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.18)',
        borderRadius: 6, padding: '7px 12px', fontSize: '0.73rem', color: '#1e40af',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <CheckCircle2 size={12} />
        Module list sourced from <code style={{ background: '#eff6ff', padding: '0 4px', borderRadius: 3 }}>UAMS_Modules</code> (read-only canonical source).
        Module structure changes must go through UAMS Master Manager.
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search modules…"
            data-testid="modules-search-input"
            style={{
              paddingLeft: 26, width: '100%', fontSize: '0.78rem',
              padding: '5px 5px 5px 26px', border: '1px solid var(--border)', borderRadius: 6,
            }}
          />
        </div>
        <button className="erp-btn" onClick={load}
          data-testid="modules-refresh-btn"
          style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={11} className={loading ? 'spin' : ''} /> Refresh
        </button>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {filtered.length} module{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['ID', 'Module Name', 'Industry', 'App Type', 'Sort', 'Scope', 'Status'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <RefreshCw size={14} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                  No modules found. Run <b>Init Tables</b> + <b>Sync</b> from the Setup tab first.
                </td>
              </tr>
            ) : filtered.map((m, idx) => {
              const isActive = m.Active || m.IsActive;
              return (
                <tr key={m.ModuleId} style={{
                  borderBottom: '1px solid var(--border)',
                  background: idx % 2 === 0 ? 'transparent' : 'var(--surface-2,#f8fafc)',
                  opacity: isActive ? 1 : 0.6,
                }}>
                  <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>
                    {m.ModuleId}
                  </td>
                  <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                    {m.ModuleName}
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: '0.72rem', color: '#64748b' }}>
                    {m.IndustryCode || 'SHARED'}
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: '0.72rem', color: '#64748b' }}>
                    {m.AppType || '1'}
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: '0.72rem', color: '#64748b', textAlign: 'center' }}>
                    {m.SortOrder ?? '—'}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {m.Scope ? (
                      <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, background: 'var(--surface-3,#f1f5f9)', color: '#475569' }}>
                        {m.Scope}
                      </span>
                    ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>—</span>}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {isActive ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: '#22c55e', fontWeight: 600 }}>
                        <CheckCircle2 size={11} /> Active
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: '#94a3b8' }}>
                        <XCircle size={11} /> Inactive
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
